import React from "react";
import { useToken } from "@packsize/authentication";
import { ConfirmationModal } from "@packsize/components";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { FormattedMessage, useIntl } from "react-intl";
import { IconButton, TextField, Typography } from "@mui/material";

import { useDeleteExternalAccessSubscription } from "@/hooks/use-external-access-subscriptions";

import { type ExternalAccessSubscription } from "../types";

interface DeleteApiKeyButtonProps {
	readonly apiKey: ExternalAccessSubscription;
}

export const DeleteApiKeyButton: React.FC<DeleteApiKeyButtonProps> = ({ apiKey: apiKey }) => {
	const intl = useIntl();
	const { decodedToken } = useToken();

	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
	const [confirmInput, setConfirmInput] = React.useState("");

	const tenantId = decodedToken?.tid;
	const deleteMutation = useDeleteExternalAccessSubscription(tenantId);

	const handleClose = (): void => {
		setShowDeleteConfirmation(false);
		setConfirmInput("");
	};

	const handleDeleteConfirmed = async (): Promise<void> => {
		if (tenantId === undefined) return;

		if (apiKey.$type === "ApiGatewayOAuthMeta") {
			await deleteMutation.mutateAsync({
				externalAccessId: apiKey.identifier,
				type: "oauth",
			});
		} else {
			await deleteMutation.mutateAsync({
				subscriptionId: apiKey.identifier,
				type: "apiKey",
			});
		}

		handleClose();
	};

	return (
		<>
			<ConfirmationModal
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				confirmText={intl.formatMessage({ id: "Delete", defaultMessage: "Delete" })}
				onCancel={handleClose}
				onClose={handleClose}
				onConfirm={handleDeleteConfirmed}
				open={showDeleteConfirmation}
				size="sm"
				title={intl.formatMessage({ id: "Delete API Connection?", defaultMessage: "Delete API Connection?" })}
				slotProps={{
					confirmButton: {
						"data-pendoid": "delete-api-key-button__button__delete-key",
						loading: deleteMutation.isPending,
						disabled: confirmInput.trim().toLowerCase() !== "delete",
					},
					cancelButton: {
						"data-pendoid": "delete-api-key-button__button__cancel",
					},
				}}
			>
				<Typography>
					<FormattedMessage
						id="deleteKey"
						defaultMessage="This will permanently delete {keyName}. This is a permanent change that cannot be undone."
						values={{ keyName: `'${apiKey?.properties?.displayName}'` }}
					/>
				</Typography>
				<TextField
					label={intl.formatMessage({ id: "deleteKeyConfirm", defaultMessage: "Please type delete to confirm" })}
					value={confirmInput}
					onChange={(e) => setConfirmInput(e.target.value)}
					fullWidth
					sx={{ mt: 2 }}
					data-pendoid="delete-api-key-button__input__confirm"
				/>
			</ConfirmationModal>
			<IconButton
				onClick={() => setShowDeleteConfirmation(true)}
				data-pendoid="delete-api-key-button__button__open-modal"
			>
				<DeleteOutlineRoundedIcon />
			</IconButton>
		</>
	);
};
